import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import { PDFDocument } from "pdf-lib";
import { TravelExpenseReport, TravelExpense } from "../types/expense";

export class PDFGenerator {
  private doc: jsPDF;

  constructor() {
    this.doc = new jsPDF();
  }

  public async generateExpenseReport(
    reportData: TravelExpenseReport,
  ): Promise<void> {
    // Set up document properties
    this.doc.setProperties({
      title: "Reisekostenabrechnung",
      subject: "Kostenabrechnung",
      author: reportData.employeeName,
      creator: "Reisekosten App",
    });

    // Add header
    await this.addHeader(reportData);

    // Add summary table first
    this.addSummaryTable(reportData);

    // Add category tables on new page
    this.doc.addPage();
    this.addCategoryTables(reportData.expenses);

    // Add signature section
    this.addSignatureSection();

    // Add footer
    this.addFooter();

    // Get the main PDF as ArrayBuffer
    const mainPdfArrayBuffer = this.doc.output("arraybuffer");

    // Check if there are any receipts to attach
    const receipts = reportData.expenses.filter((expense) => expense.receipt);

    // Log receipt status for debugging
    const expensesWithReceiptMetadata = reportData.expenses.filter(expense => expense.receiptMetadata);
    if (expensesWithReceiptMetadata.length > receipts.length) {
      console.warn(`Found ${expensesWithReceiptMetadata.length} expenses with receipt metadata but only ${receipts.length} with actual File objects. Some receipt files may not have been restored properly.`);
    }

    if (receipts.length > 0) {
      // Create final PDF with attachments
      const finalPdf = await this.createPdfWithAttachments(
        mainPdfArrayBuffer,
        receipts,
      );
      const fileName = `Reisekostenabrechnung_${reportData.employeeName.replace(/\s+/g, "_")}_${new Date().toISOString().split("T")[0]}.pdf`;
      const blob = new Blob([finalPdf], { type: "application/pdf" });
      this.downloadBlob(blob, fileName);
    } else {
      // Save without attachments
      const fileName = `Reisekostenabrechnung_${reportData.employeeName.replace(/\s+/g, "_")}_${new Date().toISOString().split("T")[0]}.pdf`;
      this.doc.save(fileName);
    }
  }

  private async createPdfWithAttachments(
    mainPdfBuffer: ArrayBuffer,
    receipts: TravelExpense[],
  ): Promise<Uint8Array> {
    const finalDoc = await PDFDocument.create();

    // Add main report pages
    const mainPdf = await PDFDocument.load(mainPdfBuffer);
    const mainPages = await finalDoc.copyPages(
      mainPdf,
      mainPdf.getPageIndices(),
    );
    mainPages.forEach((page) => finalDoc.addPage(page));

    // Add receipt pages
    for (const expense of receipts) {
      if (expense.receipt) {
        await this.addReceiptToDocument(
          finalDoc,
          expense.receipt,
          expense.description,
        );
      }
    }

    return await finalDoc.save();
  }

  private async addReceiptToDocument(
    doc: PDFDocument,
    file: File,
    description: string,
  ): Promise<void> {
    try {
      const fileBuffer = await file.arrayBuffer();

      if (file.type === "application/pdf") {
        // Handle PDF files
        const receiptPdf = await PDFDocument.load(fileBuffer);
        const receiptPages = await doc.copyPages(
          receiptPdf,
          receiptPdf.getPageIndices(),
        );

        // Add receipt pages directly
        receiptPages.forEach((page) => doc.addPage(page));
      } else if (file.type.startsWith("image/")) {
        // Handle image files
        let image;

        if (file.type === "image/jpeg" || file.type === "image/jpg") {
          image = await doc.embedJpg(fileBuffer);
        } else if (file.type === "image/png") {
          image = await doc.embedPng(fileBuffer);
        } else {
          console.warn(`Unsupported image type: ${file.type}`);
          return;
        }

        const page = doc.addPage();
        const { width, height } = page.getSize();

        // Calculate image dimensions to fit on page
        const imageAspectRatio = image.width / image.height;
        const maxWidth = width - 100;
        const maxHeight = height - 100;

        let imageWidth = maxWidth;
        let imageHeight = maxWidth / imageAspectRatio;

        if (imageHeight > maxHeight) {
          imageHeight = maxHeight;
          imageWidth = maxHeight * imageAspectRatio;
        }

        // Center the image
        const x = (width - imageWidth) / 2;
        const y = (height - imageHeight) / 2;

        page.drawImage(image, {
          x,
          y,
          width: imageWidth,
          height: imageHeight,
        });
      }
    } catch (error) {
      console.error("Error adding receipt to PDF:", error);
    }
  }

  private downloadBlob(blob: Blob, fileName: string): void {
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = fileName;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  }

  private async addHeader(reportData: TravelExpenseReport): Promise<void> {
    const leftMargin = 20;
    let yPosition = 15;

    // Logo section - Add company logo
    try {
      // Try to add the company logo image with proper aspect ratio
      // Note: Place they_consulting_logo.png in the public folder
      const logoWidth = 45;
      const logoHeight = 20; // Maintains 1500:680 aspect ratio (approximately 2.2:1)

      const headerLogoPath = `${process.env.PUBLIC_URL}/they_consulting_logo.png`;
      this.doc.addImage(
        headerLogoPath,
        "PNG",
        leftMargin,
        yPosition,
        logoWidth,
        logoHeight,
      );
      yPosition += logoHeight + 5;
    } catch (error) {
      console.warn(
        "Could not load they_consulting_logo.png, using text fallback",
      );
      // Fallback to text logo if image is not available
      this.doc.setFontSize(18);
      this.doc.setFont("helvetica", "bold");
      this.doc.setTextColor(70, 130, 180);
      this.doc.text("THEY", leftMargin, yPosition);
      yPosition += 8;
    }

    this.doc.setFontSize(9);
    this.doc.setFont("helvetica", "normal");
    this.doc.setTextColor(0, 0, 0);
    this.doc.text(
      "THEY Consulting GmbH, Kurfürstendamm 96, 10709 Berlin",
      leftMargin,
      yPosition,
    );

    // Main headline
    yPosition += 20;
    this.doc.setFontSize(10.5);
    this.doc.setFont("helvetica", "bold");
    this.doc.setTextColor(36, 68, 85);
    this.doc.text("REISEKOSTEN ABRECHNUNG", leftMargin, yPosition);

    // Info sections with better formatting
    yPosition += 20;
    this.doc.setFontSize(7.5);

    // Zeitraum section
    this.doc.setFont("helvetica", "bold");
    this.doc.setTextColor(36, 68, 85);
    this.doc.text("ZEITRAUM", leftMargin, yPosition);
    yPosition += 6;
    this.doc.setFont("helvetica", "normal");
    this.doc.setFontSize(8);
    this.doc.setTextColor(0, 0, 0);
    const quarterPeriod = reportData.quarterPeriod || "Nicht angegeben";
    this.doc.text(quarterPeriod, leftMargin, yPosition);

    // Mitarbeiter section
    yPosition += 12;
    this.doc.setFontSize(7.5);
    this.doc.setFont("helvetica", "bold");
    this.doc.setTextColor(36, 68, 85);
    this.doc.text("MITARBEITER", leftMargin, yPosition);
    yPosition += 6;
    this.doc.setFont("helvetica", "normal");
    this.doc.setFontSize(8);
    this.doc.setTextColor(0, 0, 0);
    const employeeLines = [];
    if (reportData.employeeName) {
      employeeLines.push(reportData.employeeName);
    }
    if (reportData.employeeStreet) {
      employeeLines.push(reportData.employeeStreet);
    }
    const cityLine =
      `${reportData.employeeZipCode || ""}${reportData.employeeCity ? " " + reportData.employeeCity : ""}`.trim();
    if (cityLine) {
      employeeLines.push(cityLine);
    }
    this.doc.text(employeeLines, leftMargin, yPosition);

    // Rechnungsdatum section
    yPosition += employeeLines.length * 5 + 7;
    this.doc.setFontSize(7.5);
    this.doc.setFont("helvetica", "bold");
    this.doc.setTextColor(36, 68, 85);
    this.doc.text("RECHNUNGSDATUM", leftMargin, yPosition);
    yPosition += 6;
    this.doc.setFont("helvetica", "normal");
    this.doc.setFontSize(8);
    this.doc.setTextColor(0, 0, 0);
    const currentDate = this.formatGermanDate(new Date());
    this.doc.text(currentDate, leftMargin, yPosition);

    // Remove decorative line to prevent overlap with summary table
  }

  private addSummaryTable(reportData: TravelExpenseReport): void {
    let yPosition = 150;

    // Calculate category totals
    const categoryTotals = this.calculateCategoryTotals(reportData.expenses);

    const summaryRows = Object.entries(categoryTotals).map(
      ([category, total]) => [category, this.formatCurrency(total)],
    );

    // Add grand total row
    summaryRows.push(["GESAMT", this.formatCurrency(reportData.totalAmount)]);

    // Check if summary table will fit on current page
    const adjustedYPosition = this.checkPageSpace(
      yPosition,
      summaryRows.length + 1,
    );

    // Summary table title
    this.doc.setFontSize(7.5);
    this.doc.setFont("helvetica", "bold");
    this.doc.setTextColor(36, 68, 85);
    this.doc.text("GESAMTABRECHNUNG", 20, adjustedYPosition);
    this.doc.setTextColor(0, 0, 0);

    autoTable(this.doc, {
      startY: adjustedYPosition + 5,
      head: [["KATEGORIE", "BETRAG"]],
      body: summaryRows,
      theme: "striped",
      headStyles: {
        fillColor: [142, 198, 191],
        textColor: 255,
        fontSize: 7.5,
        fontStyle: "bold",
        cellPadding: { horizontal: 3, vertical: 2 },
      },
      bodyStyles: {
        fontSize: 9,
        cellPadding: 3,
      },
      columnStyles: {
        0: { cellWidth: 120 },
        1: { cellWidth: 40, halign: "right" },
      },
      margin: { left: 20, right: 20 },
      tableWidth: 160,
      pageBreak: "avoid",
      didParseCell: (data) => {
        // Style the total row differently
        if (data.row.index === summaryRows.length - 1) {
          data.cell.styles.fontStyle = "bold";
          data.cell.styles.fillColor = [142, 198, 191]; // Same color as table header
          data.cell.styles.textColor = 255; // White text
          data.cell.styles.cellPadding = { horizontal: 3, vertical: 2 }; // Same padding as header
        }
      },
    });
  }

  private addCategoryTables(expenses: TravelExpense[]): void {
    let yPosition = 30;

    // Split expenses by category
    const belegeExpenses = expenses.filter(
      (expense) => expense.category === "Belege",
    );
    const spesenExpenses = expenses.filter(
      (expense) => expense.category === "Spesenabrechnung",
    );

    // Add Belege table if there are any
    if (belegeExpenses.length > 0) {
      yPosition = this.addBelegeTable(belegeExpenses, yPosition);
    }

    // Add Spesenabrechnung table if there are any
    if (spesenExpenses.length > 0) {
      this.addSpesenTable(spesenExpenses, yPosition);
    }
  }

  private checkPageSpace(yPosition: number, requiredRows: number): number {
    const pageHeight = this.doc.internal.pageSize.height;
    const bottomMargin = 40; // Space for footer
    const rowHeight = 12; // Approximate row height
    const headerHeight = 20; // Space for table header and title
    const requiredSpace = headerHeight + requiredRows * rowHeight;

    // If table won't fit on current page, start new page
    if (yPosition + requiredSpace > pageHeight - bottomMargin) {
      this.doc.addPage();
      return 30; // Start near top of new page
    }

    return yPosition;
  }

  private addBelegeTable(expenses: TravelExpense[], startY: number): number {
    // Check if table will fit on current page (expenses + total row + header)
    const adjustedStartY = this.checkPageSpace(startY, expenses.length + 1);

    // Table title
    this.doc.setFontSize(7.5);
    this.doc.setFont("helvetica", "bold");
    this.doc.setTextColor(36, 68, 85);
    this.doc.text("BELEGE", 20, adjustedStartY);
    this.doc.setTextColor(0, 0, 0);

    const tableRows = expenses.map((expense) => [
      expense.description,
      this.formatCurrency(expense.amount),
    ]);

    // Calculate total for this category
    const categoryTotal = expenses.reduce(
      (sum, expense) => sum + expense.amount,
      0,
    );

    // Add total row
    tableRows.push(["GESAMT", this.formatCurrency(categoryTotal)]);

    autoTable(this.doc, {
      startY: adjustedStartY + 5,
      head: [["BESCHREIBUNG", "BETRAG"]],
      body: tableRows,
      theme: "striped",
      headStyles: {
        fillColor: [142, 198, 191],
        textColor: 255,
        fontSize: 7.5,
        fontStyle: "bold",
        cellPadding: { horizontal: 3, vertical: 2 },
      },
      bodyStyles: {
        fontSize: 9,
        cellPadding: 3,
      },
      columnStyles: {
        0: { cellWidth: 120 }, // Description
        1: { cellWidth: 40, halign: "right" }, // Price
      },
      margin: { left: 20, right: 20 },
      tableWidth: 160,
      pageBreak: "avoid",
      didParseCell: (data) => {
        // Style the total row differently
        if (data.row.index === tableRows.length - 1) {
          data.cell.styles.fontStyle = "bold";
          data.cell.styles.fillColor = [142, 198, 191]; // Same color as table header
          data.cell.styles.textColor = 255; // White text
          data.cell.styles.cellPadding = { horizontal: 3, vertical: 2 }; // Same padding as header
        }
      },
    });

    return (this.doc as any).lastAutoTable.finalY + 15;
  }

  private addSpesenTable(expenses: TravelExpense[], startY: number): number {
    // Check if table will fit on current page (expenses + total row + header)
    const adjustedStartY = this.checkPageSpace(startY, expenses.length + 1);

    // Table title
    this.doc.setFontSize(7.5);
    this.doc.setFont("helvetica", "bold");
    this.doc.setTextColor(36, 68, 85);
    this.doc.text("SPESENABRECHNUNG", 20, adjustedStartY);
    this.doc.setTextColor(0, 0, 0);

    const tableRows = expenses.map((expense) => [
      expense.description,
      expense.date ? this.formatDate(expense.date) : "-",
      expense.location || "-",
      expense.duration || "-",
      this.formatCurrency(expense.amount),
    ]);

    // Calculate total for this category
    const categoryTotal = expenses.reduce(
      (sum, expense) => sum + expense.amount,
      0,
    );

    // Add total row
    tableRows.push(["GESAMT", "", "", "", this.formatCurrency(categoryTotal)]);

    autoTable(this.doc, {
      startY: adjustedStartY + 5,
      head: [["BESCHREIBUNG", "DATUM", "ORT", "DAUER", "BETRAG"]],
      body: tableRows,
      theme: "striped",
      headStyles: {
        fillColor: [142, 198, 191],
        textColor: 255,
        fontSize: 7.5,
        fontStyle: "bold",
        cellPadding: { horizontal: 3, vertical: 2 },
      },
      bodyStyles: {
        fontSize: 9,
        cellPadding: 3,
      },
      columnStyles: {
        0: { cellWidth: 50 }, // Description
        1: { cellWidth: 25 }, // Date
        2: { cellWidth: 30 }, // Location
        3: { cellWidth: 25 }, // Duration
        4: { cellWidth: 30, halign: "right" }, // Amount
      },
      margin: { left: 20, right: 20 },
      tableWidth: 160,
      pageBreak: "avoid",
      didParseCell: (data) => {
        // Style the total row differently
        if (data.row.index === tableRows.length - 1) {
          data.cell.styles.fontStyle = "bold";
          data.cell.styles.fillColor = [142, 198, 191]; // Same color as table header
          data.cell.styles.textColor = 255; // White text
          data.cell.styles.cellPadding = { horizontal: 3, vertical: 2 }; // Same padding as header
        }
      },
    });

    return (this.doc as any).lastAutoTable.finalY + 15;
  }

  private addSignatureSection(): void {
    const finalY = (this.doc as any).lastAutoTable.finalY || 150;
    let yPosition = finalY + 30;

    // Check if signature section will fit on current page
    const pageHeight = this.doc.internal.pageSize.height;
    const bottomMargin = 60; // Space for footer
    const signatureSectionHeight = 40;

    if (yPosition + signatureSectionHeight > pageHeight - bottomMargin) {
      this.doc.addPage();
      yPosition = 30;
    }

    // Add signature section title
    this.doc.setFontSize(7.5);
    this.doc.setFont("helvetica", "bold");
    this.doc.setTextColor(36, 68, 85);
    this.doc.text("UNTERSCHRIFT MITARBEITER", 20, yPosition);

    // Add signature line
    yPosition += 25;
    this.doc.setLineWidth(0.1);
    this.doc.setDrawColor(0, 0, 0);
    this.doc.line(20, yPosition, 120, yPosition); // 100px wide line

    // Add "Unterschrift" label below the line
    yPosition += 5;
    this.doc.setFontSize(8);
    this.doc.setFont("helvetica", "normal");
    this.doc.setTextColor(0, 0, 0);
    this.doc.text("Unterschrift", 20, yPosition);
  }

  private addFooter(): void {
    const pageHeight = this.doc.internal.pageSize.height;
    const pageWidth = this.doc.internal.pageSize.width;
    const totalPages = (this.doc as any).internal.getNumberOfPages();

    // Add footer to each page
    for (let i = 1; i <= totalPages; i++) {
      this.doc.setPage(i);

      // Calculate vertical center position for both logo and page number
      const footerCenterY = pageHeight - 17.5; // Center both elements vertically

      // Add logo in bottom left corner
      try {
        const footerLogoWidth = 10.5; // Reduced by 30% (15 * 0.7 = 10.5)
        const footerLogoHeight = 10.5; // Maintains 1000:1000 aspect ratio (1:1 square)

        const footerLogoPath = `${process.env.PUBLIC_URL}/they_logo.png`;
        this.doc.addImage(
          footerLogoPath,
          "PNG",
          20,
          footerCenterY - footerLogoHeight / 2, // Center logo vertically
          footerLogoWidth,
          footerLogoHeight,
        );
      } catch (error) {
        console.warn("Could not load they_logo.png in footer");
      }

      // Add page number only
      // Note: jsPDF doesn't support "Gill Sans Regular" - using helvetica as fallback
      this.doc.setFontSize(8);
      this.doc.setFont("helvetica", "normal");
      this.doc.setTextColor(150, 150, 150); // Light grey
      this.doc.text(`${i}`, pageWidth - 20, footerCenterY, {
        align: "right",
      });
      this.doc.setTextColor(0, 0, 0); // Reset to black
    }
  }

  private calculateCategoryTotals(
    expenses: TravelExpense[],
  ): Record<string, number> {
    return expenses.reduce(
      (totals, expense) => {
        totals[expense.category] =
          (totals[expense.category] || 0) + expense.amount;
        return totals;
      },
      {} as Record<string, number>,
    );
  }

  private formatDate(dateString: string): string {
    return new Date(dateString).toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  }

  private formatGermanDate(date: Date): string {
    const day = date.getDate();
    const monthNames = [
      "Januar",
      "Februar",
      "März",
      "April",
      "Mai",
      "Juni",
      "Juli",
      "August",
      "September",
      "Oktober",
      "November",
      "Dezember",
    ];
    const month = monthNames[date.getMonth()];
    const year = date.getFullYear();

    return `${day}. ${month} ${year}`;
  }

  private formatCurrency(amount: number): string {
    return (
      new Intl.NumberFormat("de-DE", {
        style: "currency",
        currency: "EUR",
      })
        .format(amount)
        .replace(/€/, "")
        .trim() + " €"
    );
  }
}

export const generateExpenseReportPDF = async (
  reportData: TravelExpenseReport,
): Promise<void> => {
  const generator = new PDFGenerator();
  await generator.generateExpenseReport(reportData);
};
